import {of as observableOf, Observable} from 'rxjs';

import {map, takeWhile, first, catchError} from 'rxjs/operators';
import {ChangeDetectorRef, Component, Input, NgZone} from '@angular/core';
import {AbstractControlComponent} from './abstract-control';
import {ViewTabComponent} from '../viewtab.component';
import {ValuesObject} from '../detail-tabconfig';
import {CacheService} from '../../../cache/cache.service';
import {DisplayAttribute} from '../../displayattribute';
import {DomSanitizer, SafeUrl} from '@angular/platform-browser';
import {LanguageService} from '../../../language/language.service';
import {ErrorService} from '../../../error/error.service';

@Component({
  selector: 'ctrl-filedownload',
  template: `
    @if (objectUrl | async) {
      <a [href]="objectUrl | async" [download]="fileName">{{ getLinkText() | async }}</a>
    }
  `,
  standalone: false,
})
export class CtrlFileDownloadComponent extends AbstractControlComponent {
  private objectBlob: Observable<Blob> = observableOf(null);
  objectUrl: Observable<SafeUrl> = observableOf(null);
  fileName: string = null;
  private linkText: string = null;
  private defaultLinkText: string = 'Download';

  @Input()
  public objectType: string = null;

  constructor(
    parent: ViewTabComponent,
    changeDetector: ChangeDetectorRef,
    ngZone: NgZone,
    private cacheService: CacheService,
    private sanitizer: DomSanitizer,
    errorService: ErrorService,
  ) {
    super(parent, 'Value', 'Value', changeDetector, ngZone, errorService);

    this.cacheService
      .getLanguageResource()
      .pipe(
        catchError((error, caught) => this.handleError(error, caught)),
        first(),
      )
      .subscribe(lr => {
        this.defaultLinkText = LanguageService.translate(
          'CtrlFileDownloadDefaultLinkText',
          'Download',
          lr,
        );
        this.fileName = LanguageService.translate('DefaultFileName', 'download', lr);
      });
  }

  getLinkText(): Observable<string> {
    if (this._config.propertyExists('Text')) {
      return this._config.getPropertyValues('Text').pipe(
        catchError((error, caught) => this.handleError(error, caught)),
        takeWhile(() => this.alive),
        map((text: any) => {
          if (text != null) {
            return String(text);
          } else {
            return this.defaultLinkText;
          }
        }),
      );
    } else {
      return observableOf(this.defaultLinkText);
    }
  }

  protected initValidators(): void {
    super.initValidators();

    let sourceObs: Observable<ValuesObject<any>> = null;
    if (this._config.propertyExists('Source')) {
      sourceObs = this._config.getProperty('Source');
    } else if (this._config.propertyExists(this.sourceAlternateName)) {
      sourceObs = this._config.getProperty(this.sourceAlternateName);
    }

    if (sourceObs != null) {
      this.sourceVoObs.subscribe((vo: ValuesObject<any>) => {
        if (vo == null || vo.Values == null) {
          this.objectBlob = observableOf(null);
          this.objectUrl = observableOf(null);
        } else if (vo.BindingSource === 'object' || vo.BindingSource === 'localization') {
          this.objectBlob = this.cacheService
            .getDisplayAttribute(vo.BindingPath, this.objectType)
            .pipe(
              first(),
              map((da: DisplayAttribute) => {
                let blob: Blob = null;
                if (da.DataType === 'Binary') {
                  blob = CtrlFileDownloadComponent.b64ToBlob(vo.Values);
                } else {
                  let mimeType: string = 'text/plain';
                  if (typeof vo.Values === 'string' && vo.Values.startsWith('<?xml')) {
                    mimeType = 'application/xml';
                  }
                  blob = CtrlFileDownloadComponent.textToBlob(vo.Values, mimeType);
                }
                return blob;
              }),
              catchError((error, caught) => this.handleError(error, caught)),
            );

          this.objectUrl = this.objectBlob.pipe(
            map((blob: Blob) => {
              if (blob == null) return null;
              return this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(blob));
            }),
            catchError((error, caught) => this.handleError(error, caught)),
          );
        } else {
          // TODO: handle non object bindings (not in display attribute)
          console.error('Can not handle non object/localization bindings for UocFileUpload', vo);
        }
      });
    }

    if (this._config.propertyExists('PromptedFileName')) {
      this._config
        .getPropertyValues('PromptedFileName')
        .pipe(
          catchError((error, caught) => this.handleError(error, caught)),
          takeWhile(() => this.alive),
        )
        .subscribe((fileName: any) => {
          if (fileName != null) {
            this.fileName = String(fileName);
          }
        });
    }
  }

  static b64ToBlob(b64Data, contentType = 'octet/stream', sliceSize = 512): Blob {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);

      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, {type: contentType});
  }

  static textToBlob(text: string, contentType = 'octet/stream'): Blob {
    return new Blob([text], {type: contentType});
  }
}
